import { axiosClient } from './useSetupAxios';

/**
 * Constructs a full URL by combining the base URL from environment variables with a relative URL.
 * @param {string} relativeUrl - The relative URL path to append to the base URL
 * @returns {string} The complete URL
 */
const getUrl = relativeUrl => `${import.meta.env.VITE_BASE_URL}${relativeUrl}`;

/**
 * Constructs a full streaming URL by combining the streaming base URL from environment variables with a relative URL.
 * @param {string} relativeUrl - The relative URL path to append to the streaming base URL
 * @returns {string} The complete streaming URL
 */
const getStreamingUrl = relativeUrl => `${import.meta.env.VITE_BASE_URL_STREAMING}${relativeUrl}`;

/**
 * Performs a GET HTTP request to the specified URL with optional query parameters.
 * @param {Object} config - Request configuration object
 * @param {string} config.url - The relative URL endpoint
 * @param {Object} [config.params={}] - Query parameters to include in the request
 * @returns {Promise} A promise that resolves to the response data
 */
export const performGetRequest = ({ url, params = {} }) => axiosClient.get(getUrl(url), { params });

/**
 * Performs a POST HTTP request to the specified URL with a payload and optional query parameters.
 * @param {Object} config - Request configuration object
 * @param {string} config.url - The relative URL endpoint
 * @param {Object} [config.payload={}] - The request body data to send
 * @param {Object} [config.params={}] - Query parameters to include in the request
 * @returns {Promise} A promise that resolves to the response data
 */
export const performPostRequest = ({ url, payload = {}, params = {} }) =>
  axiosClient.post(getUrl(url), payload, params);

/**
 * Performs a PUT HTTP request to the specified URL with a payload and optional query parameters.
 * @param {Object} config - Request configuration object
 * @param {string} config.url - The relative URL endpoint
 * @param {Object} [config.payload={}] - The request body data to send
 * @param {Object} [config.params={}] - Query parameters to include in the request
 * @returns {Promise} A promise that resolves to the response data
 */
export const performPutRequest = ({ url, payload = {}, params = {} }) =>
  axiosClient.put(getUrl(url), payload, { params });

/**
 * Performs a PATCH HTTP request to the specified URL with a payload and optional query parameters.
 * @param {Object} config - Request configuration object
 * @param {string} config.url - The relative URL endpoint
 * @param {Object} [config.payload={}] - The request body data to send
 * @param {Object} [config.params={}] - Query parameters to include in the request
 * @returns {Promise} A promise that resolves to the response data
 */
export const performPatchRequest = ({ url, payload = {}, params = {} }) =>
  axiosClient.patch(getUrl(url), payload, { params });

/**
 * Performs a DELETE HTTP request to the specified URL with optional payload and query parameters.
 * @param {Object} config - Request configuration object
 * @param {string} config.url - The relative URL endpoint
 * @param {Object} [config.payload={}] - Optional payload data to include in the request
 * @param {Object} [config.params={}] - Query parameters to include in the request
 * @returns {Promise} A promise that resolves to the response data
 */
export const performDeleteRequest = ({ url, payload = {}, params = {} }) =>
  axiosClient.delete(getUrl(url), { params: params, payload });

/**
 * Performs a streaming HTTP POST request that processes Server-Sent Events (SSE) in real-time.
 * The function handles chunked responses and provides callbacks for different streaming events.
 * Returns an abort function that can be used to cancel the streaming request.
 *
 * @param {Object} config - Request configuration object
 * @param {string} config.url - The relative URL endpoint for the streaming request
 * @param {Object} [config.payload={}] - The request body data to send
 * @param {Object} [config.params={}] - Query parameters to include in the request URL
 * @param {Function} [config.onChunk] - Callback function called for each chunk of data received.
 *   Receives the chunk string as a parameter.
 * @param {Function} [config.onComplete] - Callback function called when streaming is complete.
 *   Receives the full accumulated response as a parameter.
 * @param {Function} [config.onSuccess] - Callback function called when the stream successfully completes.
 * @param {Function} [config.onError] - Callback function called if an error occurs during streaming.
 *   Receives the error object as a parameter.
 * @returns {Object} An object with an `abort` method that can be called to cancel the streaming request.
 */
export const performStreamingRequest = ({
  url,
  payload = {},
  params = {},
  onChunk,
  onComplete,
  onSuccess,
  onError,
}) => {
  const fullUrl = getStreamingUrl(url);
  const queryParams = new URLSearchParams(params).toString();
  const finalUrl = queryParams ? `${fullUrl}?${queryParams}` : fullUrl;

  let reader = null;
  let isStreamingComplete = false;
  let isAborted = false;

  const abort = async () => {
    isAborted = true;
    if (reader) {
      try {
        await reader.cancel();
      } catch (cancelError) {
        console.warn('Error canceling reader:', cancelError);
      }
    }
  };

  (async () => {
    try {
      const response = await fetch(finalUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedResponse = '';

      while (true) {
        if (isAborted) break;

        const { done, value } = await reader.read();

        if (done) {
          // Flush any remaining bytes in the decoder buffer
          if (value && value.length > 0) {
            buffer += decoder.decode(value, { stream: false });
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (isAborted) break;
          if (line.trim() === '') continue;

          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.slice(6).trim();
              if (jsonData === '') continue;

              const data = JSON.parse(jsonData);

              if (data.error) {
                onError?.(new Error(data.error));
                return;
              }

              if (data.done) {
                isStreamingComplete = true;
                onComplete?.(data.full_response || accumulatedResponse);
                onSuccess?.();
                return;
              }

              if (data.chunk !== undefined) {
                accumulatedResponse += data.chunk;
                onChunk?.(data.chunk);
              }
            } catch (parseError) {
              console.error('Error parsing streaming response:', parseError, 'Line:', line);
              continue;
            }
          }
        }
      }

      // Process any remaining content in the buffer
      if (buffer.trim() && !isAborted) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.slice(6).trim();
              if (jsonData === '') continue;

              const data = JSON.parse(jsonData);

              if (data.error) {
                onError?.(new Error(data.error));
                return;
              }

              if (data.done) {
                isStreamingComplete = true;
                onComplete?.(data.full_response || accumulatedResponse);
                onSuccess?.();
                return;
              }

              if (data.chunk !== undefined) {
                accumulatedResponse += data.chunk;
                onChunk?.(data.chunk);
              }
            } catch (parseError) {
              console.error('Error parsing final streaming response:', parseError, 'Line:', line);
              continue;
            }
          }
        }
      }

      if (!isStreamingComplete && !isAborted) {
        onSuccess?.();
      }
    } catch (error) {
      if (!isAborted) {
        onError?.(error);
      }
    } finally {
      if (reader) {
        try {
          await reader.cancel();
        } catch (cancelError) {
          console.warn('Error canceling reader:', cancelError);
        }
      }
    }
  })();

  return { abort };
};
